import type { Auth0ContextInterface, User } from '@auth0/auth0-react';
import { datadogRum } from '@datadog/browser-rum';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useMatch,
  useRouterState,
} from '@tanstack/react-router';
import type React from 'react';
import { useEffect } from 'react';
import { FourOhFour } from './components/404';
import AlertsView from './components/alerts-view';
import { LastImport } from './components/dashboard/last-import';
import { Footer } from './components/footer';
import { Loading } from './components/loading';
import { AppNavBar } from './components/navs/app-nav-bar';
import { ProductEditModalPortal } from './components/product-table/product-edit-modal-portal';
import { RefreshingIndicator } from './components/refreshing-indicator';
import { FullPageError, RouteError, StoreError } from './components/route-errors';
import { TableSkeleton } from './components/tanstack-table/table-skeleton';
import { Updater } from './components/updater';
import { AlertContextProvider } from './contexts/alert-context';
import { isDemoStore, type Store } from './data/store';
import { compare } from './helpers/column-sort';
import { isReceivingEnabled } from './helpers/feature-flags';
import { FetchWrapper } from './helpers/fetch-wrapper';
import { ADMIN_EMAILS } from './hooks/admin-access';
import { useStoreEventListener } from './hooks/use-store-event-listener';
import { getTabId } from './hooks/use-tab-id';
import { AdminLayout } from './pages/admin/index';
import { OnboardingOverviewTab } from './pages/admin/onboarding/overview';
import { OnboardingTimelineTab } from './pages/admin/onboarding/timeline';
import { PricingTab } from './pages/admin/pricing';
import { AIAssistant } from './pages/ai-assistant/index';
import { Dashboard } from './pages/dashboard/index';
import { Onboard } from './pages/onboard/index';
import { OrderCleanup } from './pages/orders/cleanup';
import { EditOrder } from './pages/orders/edit/index';
import { Orders } from './pages/orders/index';
import { OrderSummary } from './pages/orders/summary';
import { PreviewComponents } from './pages/preview/components';
import { PreviewIndex } from './pages/preview/index';
import { PreviewTokens } from './pages/preview/tokens';
import { Products } from './pages/products/index';
import { ReceivingListPage } from './pages/receiving/index';
import { ReceivingSessionPage } from './pages/receiving/session';
import { ExceptionLogsReport } from './pages/reports/exception-logs';
import { ReportsLayout } from './pages/reports/index';
import { LowStockReport } from './pages/reports/low-stock';
import { NegativeInventoryReport } from './pages/reports/negative-inventory';
import { PhantomInventoryReport } from './pages/reports/phantom-inventory';
import { PopularityReport } from './pages/reports/popularity';
import { PreviousDaySalesReport } from './pages/reports/previous-day-sales';
import { SalesJournalReport } from './pages/reports/sales-journal';
import { StockingIssuesReport } from './pages/reports/stocking-issues';
import { UnderperformingInventoryReport } from './pages/reports/underperforming-inventory';
import { Signup } from './pages/signup';
import { Edit } from './pages/stores/edit/index';
import { Uploads } from './pages/uploads/index';
import { EditVendor } from './pages/vendors/edit';
import { Vendors } from './pages/vendors/index';
import {
  companionConnectionsQueryOptions,
  exceptionLogsQueryOptions,
  ordersQueryOptions,
  productsQueryOptions,
  receiptsQueryOptions,
  storesQueryOptions,
  uploadsQueryOptions,
  vendorsQueryOptions,
} from './query/query-options';
import { ReceivingService } from './services/receiving';
import { StoreService } from './services/stores';
import { StoreWebSocketProvider } from './websocket/store-websocket-provider';

// ---------------------------------------------------------------------------
// Router context
// ---------------------------------------------------------------------------

export interface RouterContext {
  auth: Auth0ContextInterface;
  queryClient: QueryClient;
}

// ---------------------------------------------------------------------------
// Hook for components outside the store layout that need the current store
// ---------------------------------------------------------------------------

export function useCurrentStore() {
  const storeMatch = useMatch({
    from: '/authenticated/stores/$storeId',
    shouldThrow: false,
  });
  // enabled:false — read-only subscriber; never triggers a fetch. Unlike skipToken
  // (truthy Symbol), undefined queryFn lets TanStack's fallback observer scan work.
  const { data: stores } = useQuery({ ...storesQueryOptions(), enabled: false });

  // contextStore is undefined while storeLayout.beforeLoad is still in flight.
  // Don't return a store from the live query until the route context is ready,
  // otherwise AppNavBar renders StoreMenu before storeLayout.useRouteContext()
  // has `stores`, causing a "Cannot read properties of undefined (reading 'filter')" crash.
  if (!storeMatch?.context.store) {
    return undefined;
  }

  return stores?.find((s) => s.id === storeMatch.params.storeId) ?? storeMatch.context.store;
}

// ---------------------------------------------------------------------------
// Root layout — app shell rendered by the root route
// ---------------------------------------------------------------------------

function RootLayout() {
  const store = useCurrentStore();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (store) {
      document.title = `${store.name} | Orderbot`;
      datadogRum.setUserProperty('store_product_key', store.product_key);
    } else {
      document.title = 'Orderbot';
    }
  }, [store]);

  // Skip the app shell for /signup — only show the redirect content
  if (pathname === '/signup') {
    return <Outlet />;
  }

  return (
    <AlertContextProvider>
      <RefreshingIndicator />
      <div className="h-full grid grid-rows-[auto_1fr_auto]">
        <AppNavBar />
        <div className="h-full overflow-hidden">
          <div className="h-full overflow-y-auto flex flex-col">
            <Outlet />
          </div>
        </div>
        <Footer>{store && <LastImport store={store} />}</Footer>
        <Updater />
      </div>
      <AlertsView />
    </AlertContextProvider>
  );
}

// ---------------------------------------------------------------------------
// Root route
// ---------------------------------------------------------------------------

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: FullPageError,
  notFoundComponent: FourOhFour,
});

// ---------------------------------------------------------------------------
// Public routes (no auth required)
// ---------------------------------------------------------------------------

// Dev-only design-system preview surfaces. Three pages: an index (narrative,
// Named Rules, Do's/Don'ts), a foundations page (live tokens read from CSS
// custom properties, with a demo-theme toggle), and a components catalog
// (every primitive in its real chrome). `import.meta.env.DEV` is replaced by
// Vite at build time so the route entries (and any reachable side of the
// ternary) are dead-code-eliminated from prod. The preview modules themselves
// still get bundled via the static imports above; promote to dynamic imports
// if a bundle audit ever flags them.
const previewRoutes = import.meta.env.DEV
  ? [
      createRoute({
        getParentRoute: () => rootRoute,
        path: '/preview',
        component: PreviewIndex,
      }),
      createRoute({
        getParentRoute: () => rootRoute,
        path: '/preview/tokens',
        component: PreviewTokens,
      }),
      createRoute({
        getParentRoute: () => rootRoute,
        path: '/preview/components',
        component: PreviewComponents,
      }),
    ]
  : [];

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  beforeLoad: async ({ context }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: '/onboard' });
    }

    // Fire-and-forget: the browser will navigate to Auth0 shortly.
    // Don't block — let the Signup component render "Redirecting..." message.
    context.auth.loginWithRedirect({
      appState: { returnTo: '/onboard' },
    });
  },
  component: Signup,
});

// ---------------------------------------------------------------------------
// Store classification helper
// ---------------------------------------------------------------------------

function classifyStores(stores: Store[]) {
  // Demo last so any consumer that takes `enabled[0]` as the landing target
  // prefers a real store; falls back to demo only when it's the user's sole
  // enabled store. ensureDemoStoreAccess adds the demo store to every user at
  // signup, so without this it would alphabetically win `[0]`.
  const enabled = stores
    .filter((s) => s.enabled)
    .sort((a, b) => Number(isDemoStore(a)) - Number(isDemoStore(b)));
  const disabled = stores
    .filter((s) => !s.enabled)
    .sort((a, b) => compare(new Date(b.created_at), new Date(a.created_at)));
  const needsUpload = enabled.filter((s) => s.last_uploaded_at == null);
  const ready = enabled.filter((s) => s.last_uploaded_at != null);

  return { enabled, disabled, needsUpload, ready };
}

// ---------------------------------------------------------------------------
// Authenticated layout (pathless — guards all authed routes)
// ---------------------------------------------------------------------------

const authenticatedLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  component: Outlet,
  beforeLoad: async ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      // Use pathname (not href) to avoid passing auth callback params
      // (code=, state=, error=) as the returnTo URL.
      await context.auth.loginWithRedirect({
        appState: { returnTo: location.pathname },
      });
      // Block the router until the browser navigates to Auth0.
      // loginWithRedirect() calls window.location.assign() which is async —
      // if we return, the router would continue evaluating child routes.
      await new Promise(() => {});
    }

    const storeService = new StoreService(context.auth.getAccessTokenSilently);
    // Register queryFn at the client level so query.options always has a real
    // function regardless of observer mount order. Runs once per auth session.
    context.queryClient.setQueryDefaults(storesQueryOptions().queryKey, {
      queryFn: () => storeService.getAll(),
    });
    const user = context.auth.user as User;

    return { storeService, user };
  },
});

export { authenticatedLayout };

// ---------------------------------------------------------------------------
// Index route (/) — redirect to first store
// ---------------------------------------------------------------------------

const indexRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: '/',
  beforeLoad: async ({ context }) => {
    const stores = await context.queryClient.ensureQueryData({
      ...storesQueryOptions(),
      queryFn: () => context.storeService.getAll(),
    });

    const { enabled: enabledStores, disabled: disabledStores } = classifyStores(stores);

    // No enabled stores → onboard
    if (enabledStores.length === 0) {
      // If there's a disabled store (abandoned checkout), route to it
      if (disabledStores.length > 0) {
        throw redirect({
          to: '/onboard/$storeId',
          params: { storeId: disabledStores[0].id },
          search: { checkout: undefined },
        });
      }

      throw redirect({ to: '/onboard' });
    }

    // Single-store user with no upload → onboard
    if (
      stores.length === 1 &&
      enabledStores.length === 1 &&
      enabledStores[0].last_uploaded_at == null
    ) {
      throw redirect({
        to: '/onboard/$storeId',
        params: { storeId: enabledStores[0].id },
        search: { checkout: undefined },
      });
    }

    // Has enabled store with data → dashboard
    throw redirect({
      to: '/stores/$storeId',
      params: { storeId: enabledStores[0].id },
    });
  },
});

// ---------------------------------------------------------------------------
// /onboard and /onboard/$storeId
// ---------------------------------------------------------------------------

const onboardRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: '/onboard',
  component: () => <Onboard />,
  beforeLoad: async ({ context }) => {
    const stores = await context.queryClient.ensureQueryData({
      ...storesQueryOptions(),
      queryFn: () => context.storeService.getAll(),
    });

    const { disabled: disabledStores, needsUpload } = classifyStores(stores);

    // Disabled store (abandoned checkout) — most recently created first
    if (disabledStores.length > 0) {
      throw redirect({
        to: '/onboard/$storeId',
        params: { storeId: disabledStores[0].id },
        search: { checkout: undefined },
      });
    }

    // Enabled store with no upload yet
    if (needsUpload.length > 0) {
      throw redirect({
        to: '/onboard/$storeId',
        params: { storeId: needsUpload[0].id },
        search: { checkout: undefined },
      });
    }

    // All stores fully onboarded, or no stores at all — render payment CTA
    // (user intentionally navigated here via "Add Store")

    // No stores at all — render payment CTA
  },
});

const onboardStoreRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: '/onboard/$storeId',
  component: () => {
    const { storeId } = onboardStoreRoute.useParams();

    return <Onboard storeId={storeId} />;
  },
  validateSearch: (search: Record<string, unknown>) => ({
    checkout: (search.checkout as string) || undefined,
  }),
  beforeLoad: async ({ context, params }) => {
    const stores = await context.queryClient.fetchQuery({
      ...storesQueryOptions(),
      queryFn: () => context.storeService.getAll(),
      staleTime: 0, // Always refetch — session hook can delete stores within 35 min
    });

    const storeExists = stores.some((s) => s.id === params.storeId);
    if (!storeExists) {
      throw redirect({ to: '/onboard' });
    }
  },
});

// ---------------------------------------------------------------------------
// Store layout (/stores/$storeId) — loads store, validates storeId
// ---------------------------------------------------------------------------

// Router-wide default pendingComponent. The actual culprit behind the
// "loader → skeleton → content" flicker on /products reloads was
// authenticatedLayout.beforeLoad: it calls getAccessTokenSilently (~100-250ms
// even on warm sessions) and declares no pendingComponent of its own, so the
// generic spinner showed in the content area before storeLayout/productsRoute
// got a chance to render their own skeleton.
//
// Walk the URL → routes lookup deepest-first and render the deepest matched
// route's pendingComponent. `state.matches` can't be used here because a
// parent's pending state runs *before* its children are added to it; and
// `state.location` during a transition is the committed (old) location, so we
// read `router.latestLocation` (the in-flight target) instead. Subscribing to
// `state.location.pathname` via useRouterState keeps this component reactive
// to navigation between renders.
function DelegatingPending() {
  useRouterState({ select: (s) => s.location.pathname });

  const { matchedRoutes } = router.getMatchedRoutes(router.latestLocation.pathname);
  for (let i = matchedRoutes.length - 1; i >= 0; i--) {
    const Pc = matchedRoutes[i]?.options?.pendingComponent as React.ComponentType | undefined;
    if (Pc && Pc !== DelegatingPending) {
      return <Pc />;
    }
  }

  return <Loading />;
}

export const storeLayout = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: '/stores/$storeId',
  component: StoreLayoutComponent,
  pendingMs: 200,
  pendingMinMs: 300,
  errorComponent: StoreError,
  notFoundComponent: FourOhFour,
  beforeLoad: async ({ context, params }) => {
    const stores = await context.queryClient.ensureQueryData({
      ...storesQueryOptions(),
      queryFn: () => context.storeService.getAll(),
    });

    const store = stores.find((s: Store) => s.id === params.storeId);

    if (!store) {
      const { enabled, disabled } = classifyStores(stores);
      if (enabled.length > 0) {
        throw redirect({
          to: '/stores/$storeId',
          params: { storeId: enabled[0].id },
        });
      }

      if (disabled.length > 0) {
        throw redirect({
          to: '/onboard/$storeId',
          params: { storeId: disabled[0].id },
          search: { checkout: undefined },
        });
      }

      throw redirect({ to: '/onboard' });
    }

    // Disabled store without subscription — checkout was never completed
    if (!store.enabled && !store.subscription_id) {
      throw redirect({
        to: '/onboard/$storeId',
        params: { storeId: store.id },
        search: { checkout: undefined },
      });
    }

    const fetchWrapper = new FetchWrapper(context.auth.getAccessTokenSilently);
    const receivingService = new ReceivingService(fetchWrapper, getTabId());

    return { store, stores, receivingService };
  },
});

function StoreLayoutInner() {
  useStoreEventListener();

  return (
    <>
      <Outlet />
      <ProductEditModalPortal />
    </>
  );
}

function StoreLayoutComponent() {
  const { storeId } = storeLayout.useParams();

  return (
    <StoreWebSocketProvider storeId={storeId}>
      <StoreLayoutInner />
    </StoreWebSocketProvider>
  );
}

// ---------------------------------------------------------------------------
// Store child routes
// ---------------------------------------------------------------------------

const dashboardRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/',
  component: Dashboard,
});

const storeEditRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/edit',
  component: Edit,
});

const aiAssistantRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/ai-assistant',
  component: AIAssistant,
});

const vendorsRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/vendors',
  component: Vendors,
  errorComponent: RouteError,
  pendingComponent: () => <TableSkeleton columnCount={10} />,
  loader: ({ context }) => {
    const { queryClient, storeService, store } = context;

    return Promise.all([
      queryClient.ensureQueryData({
        ...vendorsQueryOptions(store.id),
        queryFn: () => storeService.getVendorsByStore(store.id),
      }),
      queryClient.ensureQueryData({
        ...productsQueryOptions(store.id),
        queryFn: () => storeService.getProductsByStore(store.id),
      }),
    ]);
  },
});

export const vendorEditRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/vendors/$vendorId/edit',
  component: EditVendor,
  errorComponent: RouteError,
});

const productsRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/products',
  component: Products,
  errorComponent: RouteError,
  pendingComponent: () => <TableSkeleton columnCount={14} />,
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      ...productsQueryOptions(context.store.id),
      queryFn: () => context.storeService.getProductsByStore(context.store.id),
    }),
});

// ---------------------------------------------------------------------------
// Order layout (pathless) — prefetches orders, vendors, products
// ---------------------------------------------------------------------------

const orderLayout = createRoute({
  getParentRoute: () => storeLayout,
  id: 'orderLayout',
  component: Outlet,
  loader: ({ context }) => {
    const { queryClient, storeService, store } = context;

    return Promise.all([
      queryClient.ensureQueryData({
        ...ordersQueryOptions(store.id),
        queryFn: () => storeService.getOrdersByStore(store.id),
      }),
      queryClient.ensureQueryData({
        ...vendorsQueryOptions(store.id),
        queryFn: () => storeService.getVendorsByStore(store.id),
      }),
      queryClient.ensureQueryData({
        ...productsQueryOptions(store.id),
        queryFn: () => storeService.getProductsByStore(store.id),
      }),
    ]);
  },
});

const ordersRoute = createRoute({
  getParentRoute: () => orderLayout,
  path: '/orders',
  component: Orders,
  errorComponent: RouteError,
});

export const orderEditRoute = createRoute({
  getParentRoute: () => orderLayout,
  path: '/orders/$orderId/edit',
  component: EditOrder,
  errorComponent: RouteError,
});

export const orderSummaryRoute = createRoute({
  getParentRoute: () => orderLayout,
  path: '/orders/$orderId/summary',
  component: OrderSummary,
  errorComponent: RouteError,
});

export const orderCleanupRoute = createRoute({
  getParentRoute: () => orderLayout,
  path: '/orders/cleanup',
  component: OrderCleanup,
  errorComponent: RouteError,
  validateSearch: (search: Record<string, string>): { vendorId: string } => {
    if (!search.vendorId) {
      throw new Error('vendorId search param is required for order cleanup');
    }

    return { vendorId: search.vendorId };
  },
});

const uploadsRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/uploads',
  component: Uploads,
  errorComponent: RouteError,
  loader: ({ context }) => {
    const { queryClient, storeService, store } = context;

    return Promise.all([
      queryClient.ensureQueryData({
        ...uploadsQueryOptions(store.id),
        queryFn: () => storeService.getUploadsByStore(store.id),
      }),
      queryClient.ensureQueryData({
        ...companionConnectionsQueryOptions(store.id),
        queryFn: () => storeService.getCompanionConnections(store.id),
      }),
    ]);
  },
});

// ---------------------------------------------------------------------------
// Reports layout (/reports) — loads products
// ---------------------------------------------------------------------------

const reportsLayout = createRoute({
  getParentRoute: () => storeLayout,
  path: '/reports',
  component: ReportsLayout,
  errorComponent: RouteError,
});

const reportsIndexRoute = createRoute({
  getParentRoute: () => reportsLayout,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/stores/$storeId/reports/popularity',
      params: { storeId: params.storeId },
    });
  },
});

// Pathless layout that prefetches products for product-dependent reports.
// Sales Journal doesn't need products, so it's a sibling.
export const productReportsLayout = createRoute({
  getParentRoute: () => reportsLayout,
  id: 'productReportsLayout',
  component: Outlet,
  pendingComponent: Loading,
  pendingMs: 200,
  pendingMinMs: 300,
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      ...productsQueryOptions(context.store.id),
      queryFn: () => context.storeService.getProductsByStore(context.store.id),
    }),
});

const popularityRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/popularity',
  component: PopularityReport,
});

const stockingIssuesRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/stocking-issues',
  component: StockingIssuesReport,
});

const phantomInventoryRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/phantom-inventory',
  component: PhantomInventoryReport,
});

const underperformingInventoryRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/underperforming-inventory',
  component: UnderperformingInventoryReport,
});

const negativeInventoryRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/negative-inventory',
  component: NegativeInventoryReport,
});

const previousDaySalesRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/previous-day-sales',
  component: PreviousDaySalesReport,
});

const lowStockRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/low-stock',
  component: LowStockReport,
});

export const exceptionLogsRoute = createRoute({
  getParentRoute: () => productReportsLayout,
  path: '/exception-logs',
  component: ExceptionLogsReport,
  validateSearch: (search: Record<string, unknown>): { query?: string } => ({
    query: typeof search.query === 'string' ? search.query : undefined,
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      ...exceptionLogsQueryOptions(context.store.id),
      queryFn: () => context.storeService.getExceptionLogsByStore(context.store.id),
    }),
});

export const salesJournalRoute = createRoute({
  getParentRoute: () => reportsLayout,
  path: '/sales-journal',
  component: SalesJournalReport,
  validateSearch: (
    search: Record<string, unknown>
  ): { from?: string; to?: string; receipt?: string } => ({
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    receipt: search.receipt != null ? String(search.receipt) : undefined,
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      ...receiptsQueryOptions(context.store.id),
      queryFn: () => context.storeService.getReceipts(context.store.id),
    }),
});

// ---------------------------------------------------------------------------
// Admin layout (/admin) — admin email check, top-level (cross-store)
// ---------------------------------------------------------------------------

const adminLayout = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: '/admin',
  component: AdminLayout,
  errorComponent: RouteError,
  beforeLoad: ({ context }) => {
    const email = context.auth.user?.email;
    if (!email || !ADMIN_EMAILS.includes(email)) {
      throw redirect({ to: '/' });
    }
  },
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin/onboarding' });
  },
});

const pricingRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: '/pricing',
  component: PricingTab,
});

export const onboardingOverviewRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: '/onboarding',
  component: OnboardingOverviewTab,
});

export const onboardingTimelineRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: '/onboarding/$storeId',
  component: OnboardingTimelineTab,
});

// beforeLoad on both receiving routes redirects non-pilot stores to the
// dashboard. Hiding the nav item isn't enough — without this, users can reach
// /receiving by typed URL, bookmark, or stale nav history. Inlined rather than
// shared because an explicit params type on a hoisted helper would narrow the
// inferred params type for the session route and hide sessionId from
// useParams().
const receivingRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/receiving',
  component: ReceivingListPage,
  beforeLoad: ({ context, params }) => {
    if (!isReceivingEnabled(context.store)) {
      throw redirect({ to: '/stores/$storeId', params: { storeId: params.storeId } });
    }
  },
  loader: ({ context }) => {
    const { queryClient, storeService, store } = context;

    return queryClient.ensureQueryData({
      ...vendorsQueryOptions(store.id),
      queryFn: () => storeService.getVendorsByStore(store.id),
    });
  },
});

export const receivingSessionRoute = createRoute({
  getParentRoute: () => storeLayout,
  path: '/receiving/$sessionId',
  component: ReceivingSessionPage,
  beforeLoad: ({ context, params }) => {
    if (!isReceivingEnabled(context.store)) {
      throw redirect({ to: '/stores/$storeId', params: { storeId: params.storeId } });
    }
  },
});

// ---------------------------------------------------------------------------
// Route tree
// ---------------------------------------------------------------------------

export const routeTree = rootRoute.addChildren([
  ...previewRoutes,
  signupRoute,
  authenticatedLayout.addChildren([
    indexRoute,
    onboardRoute,
    onboardStoreRoute,
    storeLayout.addChildren([
      dashboardRoute,
      storeEditRoute,
      aiAssistantRoute,
      vendorsRoute,
      vendorEditRoute,
      productsRoute,
      orderLayout.addChildren([ordersRoute, orderEditRoute, orderSummaryRoute, orderCleanupRoute]),
      uploadsRoute,
      reportsLayout.addChildren([
        reportsIndexRoute,
        productReportsLayout.addChildren([
          popularityRoute,
          stockingIssuesRoute,
          phantomInventoryRoute,
          underperformingInventoryRoute,
          negativeInventoryRoute,
          previousDaySalesRoute,
          lowStockRoute,
          exceptionLogsRoute,
        ]),
        salesJournalRoute,
      ]),
      receivingRoute,
      receivingSessionRoute,
    ]),
    adminLayout.addChildren([
      adminIndexRoute,
      pricingRoute,
      onboardingOverviewRoute,
      onboardingTimelineRoute,
    ]),
  ]),
]);

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: DelegatingPending,
  defaultPendingMs: 0,
  context: {} as RouterContext,
});

// ---------------------------------------------------------------------------
// Register the router for type safety
// ---------------------------------------------------------------------------

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

declare module '@tanstack/history' {
  interface HistoryState {
    justSubmitted?: boolean;
  }
}
